import { useTranslation } from 'react-i18next';
import { 
  ShieldCheckIcon, 
  ExclamationTriangleIcon,
  NoSymbolIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  LightBulbIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { formatTimestamp } from '../../../../utils/formatTimestamp';
import React from 'react';

export interface StatusLampProps {
  label?: string;
  status: 0 | 1 | 2 | 3; // 0: Off, 1: On, 2: Blinking, 3: Reserved
  color: 'red' | 'yellow' | 'green' | 'orange';
  icon: React.ReactNode;
  popupDescription?: string;
}

export const StatusLamp = ({ label, status, color, icon, popupDescription }: StatusLampProps) => {
  // Descriptions for statuses:
  // Off (0): Lamp is inactive or no faults detected.
  // On (1): Lamp is active due to a fault.
  // Blinking (2): Lamp is blinking (intermittent fault or pending condition).
  // Reserved (3): Reserved for future use or invalid state.
  const isActive = status === 1 || status === 2;
  const isBlinking = status === 2;
  const colorMap = {
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    orange: 'bg-orange-500',
    green: 'bg-green-500'
  };

  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="relative flex flex-col items-center p-2 sm:p-4" 
      onMouseEnter={() => popupDescription && setShowTooltip(true)}
      onMouseLeave={() => popupDescription && setShowTooltip(false)}>
      <div className={`
        relative w-12 h-12 sm:w-16 sm:h-16 rounded-full border-4 border-gray-300 flex items-center justify-center
        ${isActive ? colorMap[color] : 'bg-gray-100'}
        shadow-inner transition-all duration-200
        ${isActive ? 'glow' : ''}
        ${isBlinking ? 'animate-pulse' : ''}
      `}>
        <div className="w-6 h-6 sm:w-8 sm:h-8 text-gray-900">
          {icon}
        </div>
      </div>
      {showTooltip && popupDescription && (
        <div className="absolute z-20 p-1 mt-1 w-64 bg-gray-700 text-white text-xs rounded shadow-lg whitespace-pre-line">
          {popupDescription}
        </div>
      )}
      {label && (
        <span className="mt-2 text-sm font-bold text-gray-900 tracking-wider">
          {label}
        </span>
      )}
    </div>
  );
};

export interface CentralDisplayProps {
  deviceData?: any;
}

export const CentralDisplay = (props: CentralDisplayProps) => {
  const engineHours = props?.deviceData?.engineHours?.toFixed(2) || '-';
  const tripDistance = props?.deviceData?.tripDistance?.toFixed(2) || '-';
  
  return (
    <div className="bg-white rounded-full w-40 h-40 sm:w-48 sm:h-48 flex flex-col items-center justify-center border-4 border-gray-300 p-2 sm:p-4">
      <div className="flex items-center space-x-1 sm:space-x-2 text-gray-900 mb-2">
        <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5" />
        <div>
          <div className="text-xs sm:text-sm text-gray-600">Engine Hours</div>
          <div className="text-xl sm:text-2xl font-bold">{engineHours}</div>
        </div>
      </div>
      <div className="flex items-center space-x-1 sm:space-x-2 text-gray-900">
        <ArrowTrendingUpIcon className="w-4 h-4 sm:w-5 sm:h-5" />
        <div>
          <div className="text-xs sm:text-sm text-gray-600">Trip Distance</div>
          <div className="text-xl sm:text-2xl font-bold">{tripDistance} mi</div>
        </div>
      </div>
    </div>
  );
};

interface PulseDashboardProps {
  deviceData?: any; // Consider defining a more specific type for deviceData
}

export default function PulseDashboard(props: PulseDashboardProps) {
  const [faults, setFaults] = useState<any>();
  const [helpVisible, setHelpVisible] = useState(false);
  const [aiFeedbackOpen, setAiFeedbackOpen] = useState(true); // collapsed state
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const [modalAnalysis, setModalAnalysis] = useState<null | {
    analysis: any; // Updated to 'any' for now, ideally a specific type for fault insight
    fault: any;
  }>(null);

  useEffect(() => {
    if (props.deviceData?.faults) {
      setFaults(props.deviceData.faults);
    }
  }, [props.deviceData]);
  
  const formattedTimestamp = formatTimestamp(props.deviceData?.latestTimestamp);

  // Helper to find analysis for a given fault - now directly from fault.insight
  function getFaultAnalysis(faultData: any) {
    return faultData?.insight;
  }

  const overallSeverity = props.deviceData?.digest?.severity;
  const overallConciseSummary = props.deviceData?.digest?.interpretation;
  const diagnosticRecommendations = props.deviceData?.digest?.diagnosticRecommendations || [];


  return (
    <div className="py-8 px-0 sm:p-8">
      {/* Modal for Fault Analysis */}
      {modalAnalysis && modalAnalysis.fault && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-2 sm:mx-4 relative animate-fade-in"
            style={{
              maxHeight: '90vh',
              overflowY: 'auto',
              marginTop: '100px',
              marginBottom: 'auto',
            }}
          >
            <button
              className="sticky top-0 right-0 float-right z-10 bg-white bg-opacity-90 rounded-full mt-2 mr-2 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
              onClick={() => setModalAnalysis(null)}
              aria-label="Close"
              style={{
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              }}
            >
              &times;
            </button>
            <div className="p-6 pt-2">
              <div className="flex items-center mb-3">
                <InformationCircleIcon className="w-6 h-6 text-blue-500 mr-2" />
                <span className="font-semibold text-blue-700 text-lg">Fault Analysis</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded">
                  SPN: {modalAnalysis.fault.spn}
                </span>
                <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded">
                  FMI: {modalAnalysis.fault.fmi}
                </span>
                {modalAnalysis.analysis && (
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                    modalAnalysis.analysis.severity === "HIGH" || modalAnalysis.analysis.severity === "High"
                      ? "bg-red-100 text-red-800 border border-red-300"
                      : modalAnalysis.analysis.severity === "MEDIUM" || modalAnalysis.analysis.severity === "Medium"
                      ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                      : "bg-green-100 text-green-800 border border-green-300"
                  }`}>
                    Severity: {modalAnalysis.analysis.severity}
                  </span>
                )}
                <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-0.5 rounded">
                  {modalAnalysis.fault.name}
                </span>
                <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-0.5 rounded">
                  Occurrences: {modalAnalysis.fault.oc}
                </span>
              </div>
              {modalAnalysis.analysis ? (
                <>
                  <div className="mb-4">
                    <span className="block font-semibold text-gray-700 mb-1">Interpretation</span>
                    <span className="block text-gray-800">{modalAnalysis.analysis.interpretation}</span>
                  </div>
                  <div className="mb-4">
                    <span className="block font-semibold text-gray-700 mb-1">Potential Root Causes</span>
                    <ul className="list-disc list-inside ml-3 text-gray-800 space-y-0.5">
                      {modalAnalysis.analysis.potentialRootCauses.map((cause: string, i: number) => (
                        <li key={i}>{cause}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="mb-2">
                    <span className="block font-semibold text-gray-700 mb-1">Recommended Actions</span>
                    <ol className="list-decimal list-inside ml-3 text-gray-800 space-y-0.5">
                      {modalAnalysis.analysis.recommendedImmediateActions.map((action: string, i: number) => (
                        <li key={i}>{action}</li>
                      ))}
                    </ol>
                  </div>
                </>
              ) : (
                <div className="mb-4 p-4 text-center text-gray-500">
                  No detailed analysis available for this fault.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col md:flex-row">
        {/* Left column: Dashboard */}
        <div className="w-full md:w-3/4 pr-0 md:pr-4">
          <style>
            {`
              .glow { box-shadow: 0 0 15px currentColor; }
              .dashboard-container {
                position: relative;
                width: 100%;
                height: 440px; /* Increased height to accommodate top margin */
                display: flex;
                flex-direction: column;
                align-items: center;
                padding-top: 30px; /* Add padding to push content down */
              }
              .lamp-container {
                position: relative;
                width: 340px;
                height: 100px;
                margin-top: 20px; /* Add extra top margin */
                margin-bottom: 0; /* Remove bottom margin on mobile */
              }
              @media (min-width: 640px) {
                .lamp-container {
                  margin-bottom: 10px; /* Restore margin on desktop */
                }
              }
              .lamp-position {
                position: absolute;
                width: 80px;
              }
              .lamp-position:nth-child(1) { 
                left: 0;
                top: 85px;
              }
              .lamp-position:nth-child(2) { 
                left: 70px;
                top: -10px;
              }
              .lamp-position:nth-child(3) { 
                left: 190px;
                top: -10px;
              }
              .lamp-position:nth-child(4) { 
                left: 260px;
                top: 85px;
              }
              .central-container {
                position: relative;
                z-index: 10;
                margin-top: -20px; /* Pull central display closer to lamps on mobile */
              }
              @media (min-width: 640px) {
                .central-container {
                  margin-top: 0; /* Reset margin for desktop */
                }
              }
            `}
          </style>
          <div className="dashboard-container relative">
             <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10 timestamp-container">
              <div className="flex items-center justify-center bg-white px-3 py-1 rounded-full border border-gray-300 shadow-sm w-full">
                <CalendarIcon className="w-3 h-3 mr-1 text-gray-500 flex-shrink-0" />
                <span className="text-xs text-gray-600 whitespace-nowrap">Last Updated: </span>
                <span className="text-xs font-medium ml-1 truncate">{formattedTimestamp}</span>
              </div>
            </div>
            
            {/* Help button at top-right */}
            <div className="absolute top-0 right-0">
              <div className="relative"
                onMouseEnter={() => setHelpVisible(true)}
                onMouseLeave={() => setHelpVisible(false)}>
                <button className="p-2 bg-gray-300 rounded-full text-sm font-bold w-8 h-8 flex items-center justify-center">
                  ?
                </button>
                {helpVisible && (
                  <div className="absolute right-0 mt-1 p-1 w-64 bg-gray-700 text-white text-xs rounded shadow-lg whitespace-pre-line z-50">
                    Status Descriptions:
                    {"\n"}Off: Inactive, no issues, or not used.
                    {"\n"}On: Fault detected, lamp active.
                    {"\n"}Blinking: Intermittent fault/pending condition.
                  </div>
                )}
              </div>
            </div>
            <div className="lamp-container">
              <div className="lamp-position">
                <StatusLamp 
                  label=""
                  status={props.deviceData?.lamps?.protect ?? 0}
                  color="yellow"
                  icon={<ShieldCheckIcon />}
                  popupDescription={"Protect Lamp | Indicates a system parameter has exceeded a threshold and, to avoid damage, the system is limiting functionality or performance. | Reduce load, take caution."}
                />
              </div>
              <div className="lamp-position">
                <StatusLamp 
                  label=""
                  status={props.deviceData?.lamps?.warning ?? 0} 
                  color="orange"
                  icon={<ExclamationTriangleIcon />}
                  popupDescription={"Warning Lamp | Signals a warning that the system is outside normal parameters but not in immediate danger. | Monitor, service soon."}
                />
              </div>
              <div className="lamp-position">
                <StatusLamp 
                  label=""
                  status={props.deviceData?.lamps?.stop ?? 0} 
                  color="red"
                  icon={<NoSymbolIcon />}
                  popupDescription={"Stop Lamp | Indicates a serious condition requiring immediate engine shutdown or system stop to avoid damage. | Stop operation ASAP."}
                />
              </div>
              <div className="lamp-position">
                <StatusLamp 
                  label=""
                  status={props.deviceData?.lamps?.malfunction ?? 0} 
                  color="red"
                  icon={<ExclamationCircleIcon />}
                  popupDescription={"Malfunction Lamp (MIL) | Also called 'Check Engine,' indicates an emissions-related fault or other malfunctions detected. | Service engine, check codes."}
                />
              </div>
            </div>
            <div className="central-container">
              <CentralDisplay deviceData={props.deviceData} />
            </div>
            {/* Existing: Metrics block */}
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div className="p-2 bg-white rounded shadow border border-gray-300 text-gray-900">
                <div className="text-xs text-gray-600">Engine Oil Pressure</div>
                <div className="text-lg font-bold">{props.deviceData?.engineOilPressure?.toFixed(2) ?? '-'}</div>
              </div>
              <div className="p-2 bg-white rounded shadow border border-gray-300 text-gray-900">
                <div className="text-xs text-gray-600">Engine Coolant Temperature</div>
                <div className="text-lg font-bold">{props.deviceData?.engineCoolantTemperature?.toFixed(2) ?? '-'}</div>
              </div>
              <div className="p-2 bg-white rounded shadow border border-gray-300 text-gray-900">
                <div className="text-xs text-gray-600">Total Vehicle Distance</div>
                <div className="text-lg font-bold">{props.deviceData?.totalVehicleDistance?.toFixed(2) ?? '-'}</div>
              </div>
              <div className="p-2 bg-white rounded shadow border border-gray-300 text-gray-900">
                <div className="text-xs text-gray-600">Engine Fuel Rate</div>
                <div className="text-lg font-bold">{props.deviceData?.engineFuelRate?.toFixed(2) ?? '-'}</div>
              </div>
            </div>
          </div>
        </div>
        {/* Right column: Active FMIs list repositioned for mobile */}
        <div className="w-full md:w-1/2 md:pl-4 md:border-l border-t md:border-t-0 border-gray-300 mt-4 md:mt-0">
          {/* Collapsible AI Feedback Section */}
          {overallConciseSummary && (
            <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 rounded shadow">
              <button
                className="w-full flex items-center p-4 focus:outline-none"
                onClick={() => setAiFeedbackOpen((open) => !open)}
                aria-expanded={aiFeedbackOpen}
                aria-controls="ai-feedback-panel"
              >
                <LightBulbIcon className="w-6 h-6 text-yellow-400 mr-2 flex-shrink-0" />
                <span className="font-semibold text-yellow-800 flex-1 text-left flex items-center gap-2">
                  SAMMi AI Feedback
                  {overallSeverity && (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full font-semibold text-xs border ml-2
                      ${
                        overallSeverity === "HIGH" || overallSeverity === "High"
                          ? "bg-red-100 text-red-800 border-red-300"
                          : overallSeverity === "MEDIUM" || overallSeverity === "Medium"
                          ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                          : "bg-green-100 text-green-800 border-green-300"
                      }
                    `}
                  >
                    <ExclamationCircleIcon
                      className={`w-4 h-4 mr-1 ${
                        overallSeverity === "HIGH" || overallSeverity === "High"
                          ? "text-red-500"
                          : overallSeverity === "MEDIUM" || overallSeverity === "Medium"
                          ? "text-yellow-500"
                          : "text-green-500"
                      }`}
                    />
                    {overallSeverity.toUpperCase()}
                  </span>
                  )}
                </span>
                {aiFeedbackOpen ? (
                  <ChevronDownIcon className="w-5 h-5 text-yellow-600" />
                ) : (
                  <ChevronRightIcon className="w-5 h-5 text-yellow-600" />
                )}
              </button>
              {aiFeedbackOpen && (
                <div
                  id="ai-feedback-panel"
                  className="px-4 pb-4 text-sm text-yellow-900 whitespace-pre-line"
                >
                  {overallConciseSummary}
                </div>
              )}
            </div>
          )}
          <h3 className="text-lg font-bold text-gray-900 mb-2">Active FMIs</h3>
          <div className="overflow-auto md:overflow-visible">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider"></th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">FMI</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">SPN</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Count</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Severity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {faults && Object.keys(faults).length > 0 ? (
                  Object.entries(faults).map(([key, fault]: [string, any]) => {
                    const analysis = getFaultAnalysis(fault); // Pass the whole fault object
                    // Default severity to "LOW" or some other default if not present, only if analysis exists
                    const severity = analysis?.severity || "-"; 
                    const severityClass =
                      analysis && severity.toUpperCase() === "HIGH"
                        ? "bg-red-100 text-red-700"
                        : analysis && severity.toUpperCase() === "MEDIUM"
                        ? "bg-yellow-100 text-yellow-700"
                        : analysis
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-700"; // Default for no analysis
                    return (
                      <tr key={key} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-2 py-2 text-center">
                          {analysis && ( // Only show button if analysis is available
                            <button
                              onClick={() => setModalAnalysis({ analysis, fault })}
                              className="text-blue-500 hover:text-blue-700 focus:outline-none"
                              title="View Analysis"
                            >
                              <InformationCircleIcon className="w-5 h-5" />
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{fault.fmi}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{fault.spn}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 truncate max-w-xs" title={fault.name}>
                          {fault.name}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-right">{fault.oc}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-center">
                          {analysis ? ( // Only show severity if analysis is available
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${severityClass}`}>
                              {severity.toUpperCase()}
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-700">
                              N/A
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-4 text-center text-sm text-gray-500">
                      No active faults reported.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Diagnostic Recommendations Section */}
          {diagnosticRecommendations.length > 0 && (
            <div className="mt-4">
              <button
                className="flex items-center text-sm font-semibold text-blue-700 hover:underline focus:outline-none"
                onClick={() => setDiagnosticOpen((open) => !open)}
                aria-expanded={diagnosticOpen}
                aria-controls="diagnostic-recommendations-panel"
              >
                <WrenchScrewdriverIcon className="w-5 h-5 mr-1 text-blue-400" />
                Diagnostic Recommendations
                {diagnosticOpen ? (
                  <ChevronDownIcon className="w-4 h-4 ml-1" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 ml-1" />
                )}
              </button>
              {diagnosticOpen && (
                <div
                  id="diagnostic-recommendations-panel"
                  className="mt-2 bg-blue-50 border-l-4 border-blue-300 rounded p-3 text-sm text-blue-900"
                >
                  <ul className="list-disc list-inside ml-2">
                    {diagnosticRecommendations.map((rec: string, i: number) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}